import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useScrollEffects } from '../../hooks/useScrollEffects';
import { useTerapeutas } from '../../hooks/useTerapeutas';
import './Header.css';

// SVG inline — sin dependencia de Font Awesome
const ChevronDownIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
    <path d="M3 2L7 5L3 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { isHeaderScrolled, isHeaderVisible, scrollToElement } = useScrollEffects();
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLLIElement>(null);

  const { terapeutasExtended, loading: terapeutasLoading } = useTerapeutas();

  // Cierra todo al cambiar de ruta
  useEffect(() => {
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
    document.body.classList.remove('menu-open');
  }, [location.pathname]);

  // Escape + click fuera del dropdown
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsDropdownOpen(false);
        if (isMobileMenuOpen) {
          setIsMobileMenuOpen(false);
          document.body.classList.remove('menu-open');
        }
      }
    };

    const handleMouseDown = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [isMobileMenuOpen]);

  // Viewport height para Safari mobile
  useEffect(() => {
    const setVh = () => {
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    };
    setVh();
    window.addEventListener('resize', setVh);
    window.addEventListener('orientationchange', setVh);
    return () => {
      window.removeEventListener('resize', setVh);
      window.removeEventListener('orientationchange', setVh);
    };
  }, []);

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsDropdownOpen(false);
    document.body.classList.remove('menu-open');
  };

  const handleMobileToggle = () => {
    const next = !isMobileMenuOpen;
    setIsMobileMenuOpen(next);
    document.body.classList.toggle('menu-open', next);
    if (!next) setIsDropdownOpen(false);
  };

  const handleAnchorNav = (anchor: string) => {
    closeMobileMenu();
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => scrollToElement(anchor), 100);
    } else {
      scrollToElement(anchor);
    }
  };

  const handleRouteNav = (path: string) => {
    closeMobileMenu();
    navigate(path);
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    handleAnchorNav('#home');
  };

  const handleTherapistClick = (slug: string) => {
    closeMobileMenu();
    navigate(`/terapista/${slug}`);
  };

  const handleViewAll = (e: React.MouseEvent) => {
    e.preventDefault();
    handleAnchorNav('#professionals');
  };

  const headerClasses = [
    'site-header',
    isHeaderScrolled ? 'site-header--scrolled' : '',
    !isHeaderVisible ? 'site-header--hidden' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <header className={headerClasses} id="header">
      <div className="site-header__inner">
        {/* Logo */}
        <a href="/" className="site-logo" onClick={handleLogoClick} aria-label="Conectemos Chile — inicio">
          <span className="site-logo__mark">
            <img
              src="/assets/img/logo.png"
              alt=""
              className="site-logo__img"
              width="48"
              height="48"
            />
          </span>
          <span className="site-logo__text">Conectemos Chile</span>
        </a>

        {/* Nav desktop */}
        <nav className="site-nav" aria-label="Navegación principal">
          <ul className="site-nav__list" role="menubar">
            {/* Dropdown Profesionales */}
            <li className="site-nav__item site-nav__item--dropdown" ref={dropdownRef} role="none">
              <button
                className={`site-nav__link site-nav__link--dropdown${isDropdownOpen ? ' is-open' : ''}`}
                aria-expanded={isDropdownOpen}
                aria-haspopup="true"
                onClick={() => setIsDropdownOpen((v) => !v)}
                type="button"
              >
                Profesionales
                <span className={`site-nav__chevron${isDropdownOpen ? ' is-rotated' : ''}`}>
                  <ChevronDownIcon />
                </span>
              </button>

              <div
                className={`site-dropdown${isDropdownOpen ? ' is-open' : ''}`}
                role="menu"
                aria-label="Lista de profesionales"
              >
                {terapeutasLoading ? (
                  <p className="site-dropdown__loading">Cargando...</p>
                ) : (
                  <ul className="site-dropdown__list">
                    {terapeutasExtended.slice(0, 4).map((t) => (
                      <li key={t.id} role="none">
                        <button
                          className="site-dropdown__card"
                          role="menuitem"
                          type="button"
                          aria-label={`Ver perfil de ${t.name}`}
                          onClick={() => handleTherapistClick(t.id)}
                        >
                          <img
                            src={t.imageSmall}
                            alt=""
                            className="site-dropdown__avatar"
                            width="36"
                            height="36"
                            loading="lazy"
                          />
                          <span className="site-dropdown__info">
                            <span className="site-dropdown__name">{t.name}</span>
                            <span className="site-dropdown__title">{t.title.split(',')[0]}</span>
                          </span>
                          <span className="site-dropdown__arrow" aria-hidden="true">
                            <ChevronRightIcon />
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <a
                  href="#professionals"
                  className="site-dropdown__view-all"
                  role="menuitem"
                  onClick={handleViewAll}
                >
                  Ver todos los profesionales →
                </a>
              </div>
            </li>

            <li role="none">
              <a
                href="#prices"
                className="site-nav__link"
                role="menuitem"
                onClick={(e) => { e.preventDefault(); handleAnchorNav('#prices'); }}
              >
                Precios
              </a>
            </li>

            <li role="none">
              <a
                href="/blog"
                className="site-nav__link"
                role="menuitem"
                onClick={(e) => { e.preventDefault(); handleRouteNav('/blog'); }}
              >
                Blog
              </a>
            </li>

            <li role="none">
              <a
                href="#contact"
                className="site-nav__link"
                role="menuitem"
                onClick={(e) => { e.preventDefault(); handleAnchorNav('#contact'); }}
              >
                Contacto
              </a>
            </li>
          </ul>
        </nav>

        {/* CTA desktop */}
        <a
          href="/agendar"
          className="site-header__cta"
          onClick={(e) => { e.preventDefault(); handleRouteNav('/agendar'); }}
        >
          Agendar Hora
        </a>

        {/* Hamburger */}
        <button
          className={`site-hamburger${isMobileMenuOpen ? ' is-open' : ''}`}
          aria-label={isMobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
          aria-expanded={isMobileMenuOpen}
          onClick={handleMobileToggle}
          type="button"
        >
          <span className="site-hamburger__bar" />
          <span className="site-hamburger__bar" />
          <span className="site-hamburger__bar" />
        </button>
      </div>

      {/* Panel mobile */}
      <div
        className={`site-mobile-panel${isMobileMenuOpen ? ' is-open' : ''}`}
        aria-hidden={!isMobileMenuOpen}
      >
        <nav aria-label="Menú móvil">
          <ul className="site-mobile-panel__list">
            {/* Profesionales expandible */}
            <li className="site-mobile-panel__item">
              <button
                className="site-mobile-panel__link site-mobile-panel__link--toggle"
                type="button"
                aria-expanded={isDropdownOpen}
                onClick={() => setIsDropdownOpen((v) => !v)}
              >
                Profesionales
                <span className={`site-nav__chevron${isDropdownOpen ? ' is-rotated' : ''}`}>
                  <ChevronDownIcon />
                </span>
              </button>

              <div className={`site-mobile-dropdown${isDropdownOpen ? ' is-open' : ''}`}>
                {terapeutasLoading ? (
                  <p className="site-dropdown__loading">Cargando...</p>
                ) : (
                  <>
                    {terapeutasExtended.slice(0, 4).map((t) => (
                      <button
                        key={t.id}
                        className="site-dropdown__card site-dropdown__card--mobile"
                        type="button"
                        aria-label={`Ver perfil de ${t.name}`}
                        onClick={() => handleTherapistClick(t.id)}
                      >
                        <img
                          src={t.imageSmall}
                          alt=""
                          className="site-dropdown__avatar"
                          width="36"
                          height="36"
                          loading="lazy"
                        />
                        <span className="site-dropdown__info">
                          <span className="site-dropdown__name">{t.name}</span>
                          <span className="site-dropdown__title">{t.title.split(',')[0]}</span>
                        </span>
                        <span className="site-dropdown__arrow" aria-hidden="true">
                          <ChevronRightIcon />
                        </span>
                      </button>
                    ))}
                    <a
                      href="#professionals"
                      className="site-dropdown__view-all"
                      onClick={handleViewAll}
                    >
                      Ver todos los profesionales →
                    </a>
                  </>
                )}
              </div>
            </li>

            <li className="site-mobile-panel__item">
              <a
                href="#prices"
                className="site-mobile-panel__link"
                onClick={(e) => { e.preventDefault(); handleAnchorNav('#prices'); }}
              >
                Precios
              </a>
            </li>

            <li className="site-mobile-panel__item">
              <a
                href="/blog"
                className="site-mobile-panel__link"
                onClick={(e) => { e.preventDefault(); handleRouteNav('/blog'); }}
              >
                Blog
              </a>
            </li>

            <li className="site-mobile-panel__item">
              <a
                href="#contact"
                className="site-mobile-panel__link"
                onClick={(e) => { e.preventDefault(); handleAnchorNav('#contact'); }}
              >
                Contacto
              </a>
            </li>
          </ul>

          {/* CTA mobile */}
          <div className="site-mobile-panel__cta-wrapper">
            <a
              href="/agendar"
              className="site-mobile-panel__cta"
              onClick={(e) => { e.preventDefault(); handleRouteNav('/agendar'); }}
            >
              Agendar Hora
            </a>
          </div>
        </nav>
      </div>

      {/* Overlay */}
      {isMobileMenuOpen && (
        <div
          className="site-overlay"
          aria-hidden="true"
          onClick={closeMobileMenu}
        />
      )}
    </header>
  );
};

export default Header;
